/**
 * reportContentBuilder.ts — M6 Output Factory · Increment 3 (2026-06-25, Anil).
 *
 * PURE — turns the intake answers + the confirmed Findings Pool into an ordered
 * list of readable report SECTIONS, each tagged with its source (auto/manual/
 * composite) and which finding/data it came from. This is what makes a REAL,
 * readable report viewable in the preview (the production PDF render lives in the
 * functions composer; this is the portal-side, no-Firebase mirror used by the
 * preview + the pick/drag composer).
 *
 * Sections render the AD-040/041/042 structured intake data — Design Programme
 * (A–H) · Construction details (11 groups) · Coordination team — and the
 * confirmed Astro-Vastu / Health / Verdict FINDINGS. Empty data → the section is
 * omitted (so a sparse report stays clean).
 *
 * Tier governs the COMPOSER, not this builder: this produces the FULL candidate
 * section set + their default-selected state per tier; the composer (L1 locked /
 * L2 curated / L3 open) decides which the staff may toggle.
 *
 * Firebase-free + React-free → unit-tested in the `.cjs` runner. The section
 * `body` is a small structured model (rows/lists/text) the renderer turns into
 * readable HTML/JSX — NOT a raw HTML string (no injection surface).
 */
import type { Finding, FindingType } from './findingsModel';
import { isPoolReady } from './findingsModel';
import { tierLevel } from './reportPhases';

export type ReportSectionSource = 'auto' | 'manual' | 'composite';

/** A row in a section body (label + value, or a sub-list). */
export interface SectionRow {
  label?: string;
  value?: string;
  list?: string[];
  /** A table (header + rows of cells). */
  table?: { head: string[]; rows: string[][] };
}

export interface ReportSection {
  /** Stable key (used by the composer's select set). */
  key: string;
  /** Display title (e.g. "Design programme"). */
  title: string;
  /** Which finding type / data this section draws from (for the source chip). */
  source: ReportSectionSource;
  /** Was this assembled from a CONFIRMED finding (pool-ready)? auto-data is always ok. */
  fromConfirmedFinding: boolean;
  /** The readable body — rows the renderer formats. */
  body: SectionRow[];
  /** Default-selected for this tier (the composer starts with these picked). */
  defaultSelected: boolean;
  /** Increment-3b flag: rendered from a confirmed RECORD whose deep engine data
   * isn't wired yet (astro/health) — the report shows it but it's a stub-depth. */
  stubDepth?: boolean;
}

/* ── helpers ── */
function t(v: unknown): string { return v == null ? '' : String(v).trim(); }
function nonEmpty(rows: SectionRow[]): boolean {
  return rows.some((r) => t(r.value) || (r.list && r.list.length) || (r.table && r.table.rows.length));
}

type Answers = Record<string, unknown>;

/** PURE — Design Programme (A–H) → a report section, or null when empty. */
function designProgrammeSection(a: Answers, selected: boolean): ReportSection | null {
  const dp = (a.designProgramme || {}) as Record<string, unknown>;
  const spaces = Array.isArray(dp.spaces) ? (dp.spaces as Record<string, unknown>[]).filter((s) => t(s.space) || t(s.targetArea)) : [];
  const rows: SectionRow[] = [];
  if (spaces.length) {
    rows.push({ table: { head: ['Space', 'Qty', 'Target area', 'Floor', 'Priority'], rows: spaces.map((s) => [t(s.space), t(s.qty) || '1', t(s.targetArea) || '—', t(s.floor) || '—', t(s.priority)]) } });
  }
  const lifestyle = [t(dp.householdType), t(dp.wfh) && `WFH: ${t(dp.wfh)}`, t(dp.hosting) && `Hosting: ${t(dp.hosting)}`].filter(Boolean);
  if (lifestyle.length) rows.push({ label: 'Lifestyle', value: lifestyle.join(' · ') });
  if (Array.isArray(dp.futureProofing) && dp.futureProofing.length) rows.push({ label: 'Future-proofing', list: dp.futureProofing as string[] });
  const style = [t(dp.archStyle), t(dp.interiorLeaning)].filter(Boolean);
  if (style.length) rows.push({ label: 'Style', value: style.join(' · ') });
  const scope = [t(dp.scopeOfWork), t(dp.budgetRange), t(dp.phasing), t(dp.desiredStart)].filter(Boolean);
  if (scope.length) rows.push({ label: 'Scope & budget', value: scope.join(' · ') });
  const constraints = [(Array.isArray(dp.constraints) ? (dp.constraints as string[]).join(', ') : ''), t(dp.mustKeeps)].filter(Boolean);
  if (constraints.length) rows.push({ label: 'Constraints / must-keeps', value: constraints.join(' · ') });
  if (!nonEmpty(rows)) return null;
  return { key: 'design_programme', title: 'Design programme', source: 'manual', fromConfirmedFinding: false, body: rows, defaultSelected: selected };
}

/** PURE — Coordination team (H) → section, or null. */
function coordinationSection(a: Answers, selected: boolean): ReportSection | null {
  const dp = (a.designProgramme || {}) as Record<string, unknown>;
  const co = Array.isArray(dp.coordination) ? (dp.coordination as Record<string, unknown>[]).filter((m) => t(m.name) || t(m.contactId)) : [];
  if (!co.length) return null;
  const rows: SectionRow[] = co.map((m) => ({ label: t(m.role) || 'Team', value: [t(m.name) || t(m.contactId), t(m.email), t(m.whatsapp)].filter(Boolean).join(' · ') }));
  return { key: 'coordination', title: 'Coordination · project team', source: 'manual', fromConfirmedFinding: false, body: rows, defaultSelected: selected };
}

/** PURE — Construction details (11 groups) → section, or null. */
function constructionSection(a: Answers, selected: boolean): ReportSection | null {
  const cd = (a.constructionDetails || {}) as Record<string, unknown>;
  const line = (label: string, parts: (string | undefined)[]) => {
    const v = parts.filter(Boolean).map((x) => t(x)).filter(Boolean).join(' · ');
    return v ? { label, value: v } : null;
  };
  const rows = ([
    line('Stage / structure', [t(cd.drawingStage), t(cd.structuralSystem), t(cd.floors), t(cd.builtUpSqft) && `${t(cd.builtUpSqft)} sq ft`]),
    Array.isArray(cd.prepared) && cd.prepared.length ? { label: 'Prepared', list: cd.prepared as string[] } : null,
    line('Water & plumbing', [t(cd.waterSource), t(cd.ugTank) && `UG:${t(cd.ugTank)}`, t(cd.ohTank) && `OH:${t(cd.ohTank)}`, t(cd.waterNotes)]),
    line('Electrical & power', [t(cd.elecSource), t(cd.backupPower), t(cd.solarProvision)]),
    line('HVAC', [t(cd.hvacApproach), t(cd.hvacAreas), t(cd.hvacNotes)]),
    line('Ceiling & lighting', [t(cd.falseCeiling), t(cd.lightingApproach)]),
    line('Materials / MEP grade', [t(cd.specLevel), t(cd.plumbingGrade) && `plumb:${t(cd.plumbingGrade)}`, t(cd.electricalGrade) && `elec:${t(cd.electricalGrade)}`, t(cd.materialNotes)]),
    line('Doors & windows', [t(cd.windowSystem), t(cd.doorTypes)]),
    line('Soil & geology', [t(cd.soilType), t(cd.geotech), t(cd.waterTable), t(cd.soilNotes)]),
    line('Construction tech', [t(cd.constructionMethod), t(cd.localMaterialNotes)]),
    line('Approvals / site', [t(cd.statutoryApproval), t(cd.siteCondition), t(cd.targetStart), t(cd.specificNote)]),
  ].filter(Boolean)) as SectionRow[];
  if (!nonEmpty(rows)) return null;
  return { key: 'construction_details', title: 'Construction details', source: 'manual', fromConfirmedFinding: false, body: rows, defaultSelected: selected };
}

/**
 * PURE — the VERDICT section from the SINGLE verdict store `answers.verdict`
 * (+ `answers.verdict_reasoning`), 2.3. The verdict is NOT a findings-pool record
 * (that `verdict` finding was unwired + dropped from the L1A checklist); it is
 * owned by Step 5 (saveVerdict) and read here + by the deployed report — one
 * store, no divergence. Null when blank (the report keeps its §D placeholder).
 */
function verdictSection(a: Answers, selected: boolean): ReportSection | null {
  const statement = t(a.verdict);
  const reasoning = t(a.verdict_reasoning);
  if (!statement && !reasoning) return null;
  const rows: SectionRow[] = [];
  if (statement) rows.push({ value: statement });
  if (reasoning) {
    const paras = reasoning.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
    if (paras.length) rows.push({ list: paras });
  }
  return {
    key: 'verdict',
    title: 'Verdict & recommendation',
    source: 'manual',
    fromConfirmedFinding: false,
    body: rows,
    defaultSelected: selected,
  };
}

/** PURE — a confirmed FINDING → section (auto/composite, pool-ready only). */
function findingSection(
  findings: Finding[],
  type: FindingType,
  title: string,
  source: ReportSectionSource,
  selected: boolean,
  stubDepth: boolean,
): ReportSection | null {
  const f = findings.find((x) => x.type === type);
  if (!f || !isPoolReady(f.status)) return null; // only CONFIRMED findings reach the report
  const p = (f.payload || {}) as Record<string, unknown>;
  const rows: SectionRow[] = [];
  if (t(p.summary)) rows.push({ value: t(p.summary) });
  if (Array.isArray(p.readings) && p.readings.length) rows.push({ list: p.readings as string[] });
  if (Array.isArray(p.remedies) && p.remedies.length) rows.push({ label: 'Remedies', list: p.remedies as string[] });
  if (Array.isArray(p.notes) && p.notes.length) rows.push({ list: p.notes as string[] });
  if (!rows.length) rows.push({ value: `${title} confirmed for this project.` });
  return { key: type, title, source: f.source || source, fromConfirmedFinding: true, body: rows, defaultSelected: selected, ...(stubDepth ? { stubDepth: true } : {}) };
}

/**
 * PURE — build the full candidate section list for a tier from intake answers +
 * the confirmed findings. The COMPOSER then governs which are selectable per tier
 * (L1 locked = all default-on & non-toggleable; L2 curated; L3 open). Sections
 * are ordered: brief/programme → construction → astro-vastu → health → verdict →
 * coordination.
 */
export function buildReportSections(
  tier: string,
  answers: Answers,
  findings: Finding[],
): ReportSection[] {
  const level = tierLevel(tier);
  // Default selection: L1 = a tight frozen set; L2/L3 = everything available.
  const selAll = level >= 2;
  const out: (ReportSection | null)[] = [
    designProgrammeSection(answers, selAll),
    constructionSection(answers, selAll),
    findingSection(findings, 'zone_auo', 'Zone / AUO analysis', 'auto', true, false),
    findingSection(findings, 'astro_vastu', 'Astro-Vastu chart & readings', 'auto', selAll, true),
    findingSection(findings, 'astro_remedies', 'Astrology-based remedies', 'composite', selAll, true),
    findingSection(findings, 'health_overlay', 'Health overlay', 'manual', selAll, true),
    // 2.3 — the verdict is read from the SINGLE store (answers.verdict), not the
    // (now dropped) verdict finding — one store, no divergence.
    verdictSection(answers, true),
    coordinationSection(answers, selAll),
  ];
  return out.filter((s): s is ReportSection => s !== null);
}
